import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { getWithAuth } from 'services/http.service';
import { GET_ROLE_PERMISSIONS } from 'services/url.service';
import { ADMIN_ROLES, DEFAULT_SECTION_ROLES, USER_ROLES } from 'utils/roles';

const RolePermissionsContext = createContext(null);

function buildDefaultPermissions() {
    return {
        [USER_ROLES.MANAGER]: {
            sections: Object.entries(DEFAULT_SECTION_ROLES)
                .filter(([, roles]) => roles.includes(USER_ROLES.MANAGER))
                .map(([section]) => section),
        },
        [USER_ROLES.STAFF]: {
            sections: Object.entries(DEFAULT_SECTION_ROLES)
                .filter(([, roles]) => roles.includes(USER_ROLES.STAFF))
                .map(([section]) => section),
        },
    };
}

export function RolePermissionsProvider({ children }) {
    const { data: session } = useSession();
    const [rolePermissions, setRolePermissions] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const role = session?.user?.role;
    const isAdmin = ADMIN_ROLES.includes(role);

    const fetchPermissions = useCallback(async () => {
        if (!isAdmin) return;
        setIsLoading(true);
        try {
            const response = await getWithAuth(GET_ROLE_PERMISSIONS);
            if (response?.entity) {
                setRolePermissions(response.entity);
            } else {
                setRolePermissions(buildDefaultPermissions());
            }
        } catch (_) {
            setRolePermissions(buildDefaultPermissions());
        } finally {
            setIsLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (session && isAdmin) fetchPermissions();
    }, [session, isAdmin, fetchPermissions]);

    return (
        <RolePermissionsContext.Provider value={{ rolePermissions, isLoading, refetch: fetchPermissions }}>
            {children}
        </RolePermissionsContext.Provider>
    );
}

export function useRolePermissions() {
    return useContext(RolePermissionsContext);
}
